import Head from "expo-router/head";
import { useState } from "react";
import { Linking, Platform, StyleSheet, View } from "react-native";

import { useTranslation } from "@luna-restaurant/i18n";
import type {
  DishNutritionDataset,
  ParticipantList,
} from "@luna-restaurant/school-api";
import {
  Button,
  PageContainer,
  ThemedHeadlineText,
  ThemedTextWithBold,
  ThemedTitle,
  useCustomTheme,
} from "@luna-restaurant/ui";

import { ContactBanner } from "@/components/ContactBanner";
import { useCollection } from "@/hooks/useCollection";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useRequest } from "@/hooks/useRequest";

const ContainerItem = ({
  title,
  subtitle,
  buttonText,
  onPress,
}: {
  title: string;
  subtitle: string;
  buttonText: string;
  onPress: () => void;
}) => {
  const { colors, fonts } = useCustomTheme();
  const [isHovered, setIsHovered] = useState(false);
  const hoverColor = colors.palette.spinach50;
  const activeIconColor = isHovered ? hoverColor : undefined;
  const activeButtonStyle = isHovered ? { borderColor: hoverColor } : {};

  return (
    <View style={styles.containerItem}>
      <ThemedHeadlineText headline={title} />
      <ThemedTextWithBold textContent={subtitle} style={styles.subtitle} />
      <Button
        buttonText={buttonText}
        iconName="download"
        iconPosition="left"
        iconSize={24}
        buttonStyle={[styles.button, activeButtonStyle]}
        buttonTextStyle={[fonts.bold, styles.buttonText]}
        iconColor={activeIconColor}
        onHoverIn={() => {
          setIsHovered(true);
        }}
        onHoverOut={() => {
          setIsHovered(false);
        }}
        onPress={onPress}
      />
    </View>
  );
};

export default function CurrentListsScreen() {
  const { t, i18n } = useTranslation();
  const { currentUser } = useCurrentUser();
  const schoolPathSegment = currentUser?.school;
  const request = useRequest();

  const participantsListQuery = useCollection<ParticipantList>({
    queryKey: ["participants-list"],
    path: `${schoolPathSegment}/participant-lists`,
    enabled: !!schoolPathSegment,
    staleTime: 6 * 60 * 60 * 1000,
  });

  const participantsLists = participantsListQuery.items;

  const downloadParticipantsList = async () => {
    if (
      participantsListQuery.isLoading ||
      !participantsLists?.[0] ||
      Platform.OS !== "web"
    )
      return;
    const fileName = t("currentLists.leftContainerHeadline.fileName", {
      date: new Date().toLocaleDateString(i18n.language, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
    });

    const blob = await request<Blob>(participantsLists[0]["@id"], {
      headers: { Accept: "application/pdf" },
    });
    if (!blob) return;

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const dishNutritionDatasetsQuery = useCollection<DishNutritionDataset>({
    queryKey: ["dish-nutrition-datasets"],
    path: `/v1/dish-nutrition-datasets`,
    staleTime: 6 * 60 * 60 * 1000,
  });

  const dishNutritionDatasets = dishNutritionDatasetsQuery.items;

  const downloadDishNutritionDatasets = () => {
    if (dishNutritionDatasetsQuery.isLoading || !dishNutritionDatasets?.[0])
      return;
    const link = dishNutritionDatasets[0].file;
    const fileName = t("currentLists.rightContainerHeadline.fileName", {
      date: new Date().toLocaleDateString(i18n.language, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
    });
    Linking.openURL(`${link}${fileName ? `/${fileName}` : ""}?download=true`);
  };

  const header = (
    <ThemedTitle
      title={t("currentLists.title")}
      subtitle={t("currentLists.subtitle")}
      icon={{ iconSet: "material-community", icon: "format-list-bulleted" }}
    />
  );

  return (
    <>
      <Head>
        <title>{t("titles.currentLists")}</title>
      </Head>
      <PageContainer header={header} style={styles.pageContainer}>
        <View style={styles.container}>
          {participantsLists?.[0]?.id && Platform.OS === "web" && (
            <ContainerItem
              title={t("currentLists.leftContainerHeadline.title")}
              subtitle={t("currentLists.leftContainerHeadline.subtitle")}
              buttonText={t("currentLists.leftContainerHeadline.buttonText")}
              onPress={downloadParticipantsList}
            />
          )}
          {dishNutritionDatasets?.[0]?.file && (
            <ContainerItem
              title={t("currentLists.rightContainerHeadline.title")}
              subtitle={t("currentLists.rightContainerHeadline.subtitle")}
              buttonText={t("currentLists.rightContainerHeadline.buttonText")}
              onPress={downloadDishNutritionDatasets}
            />
          )}
        </View>
      </PageContainer>
      <ContactBanner />
    </>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    flex: 1,
    gap: 120,
  },
  container: {
    flexDirection: "row",
    gap: 120,
    paddingBottom: 64,
  },
  containerItem: {
    flex: 1,
    gap: 32,
  },
  subtitle: {
    fontSize: 16,
    lineHeight: 20.8,
  },
  button: {
    alignSelf: "flex-start",
    borderWidth: 2,
    paddingHorizontal: 16,
    height: 56,
    gap: 0,
  },
  buttonText: {
    fontSize: 16,
    lineHeight: 20.8,
    paddingHorizontal: 8,
  },
});
